import { taskSnapshotsEqual } from '../../supabase/functions/_shared/taskActionDomain';
import { agendaOwnerSession, invokeAgendaAction, hasConfirmedAgendaReceiptResult, isConfirmedAgendaReceiptFailure,
  mirrorAgendaReceiptReference, agendaOperationKey, readAgendaPromotionTask, AgendaExecutorError, type AgendaResponse, type LocalAgendaReceiptReference } from '@/lib/agendaExecutor';
import { claimConnectorWrite, finishConnectorWrite, hasConfirmedConnectorResult, type ConnectorWriteBody } from '@/lib/connectorPendingWrite';
import { resolvePreviousAgendaWrite } from '@/lib/resolvePreviousAgendaWrite';
import { publishAgendaRefresh } from '@/lib/agendaChanges';
import { parseAgendaActionRequest, type AgendaRequest } from '../../supabase/functions/_shared/agendaActionDomain';

interface Options {
  ownerId: string; request: AgendaRequest; language: string; isCurrent: () => boolean;
  onReceipt?: (receipt: LocalAgendaReceiptReference) => void | Promise<boolean>;
}

/** One explicit press of Save owns this immutable request. Existing journals
 * are receipt-only recovery; an unknown outcome is never a new creation. */
export async function saveReviewedAgendaCreate(options: Options): Promise<AgendaResponse> {
  const request = parseAgendaActionRequest(structuredClone(options.request));
  if (!['create','create_batch','promote_task'].includes(request.action) || request.confirmed !== true) throw new Error('review_required');
  const assert = () => { if (!options.isCurrent()) throw new Error('invalid_session'); };
  assert(); await agendaOwnerSession(options.ownerId, assert); assert();
  const ready = await resolvePreviousAgendaWrite({ ownerId: options.ownerId, operationId: request.operation_id!,
    language: options.language, isCurrent: options.isCurrent });
  assert(); if (!ready) throw new Error('agenda_previous_pending');
  const body = { ...request, idempotency_key: agendaOperationKey(String(request.operation_id), request.action) } as ConnectorWriteBody;
  const claim = await claimConnectorWrite(options.ownerId, 'local_agenda', body,
    { title: request.title ?? request.events?.[0]?.title }, undefined, assert);
  assert();
  const entry = structuredClone(claim.entry);
  if (entry.ownerId !== options.ownerId || entry.group !== 'local_agenda' || JSON.stringify(entry.body) !== JSON.stringify(body)) throw new Error('agenda_result_unconfirmed');
  if (claim.created && request.action === 'promote_task') {
    const task = await readAgendaPromotionTask(request.task_id!, options.ownerId, assert);
    if (!task.writable || !task.snapshot || !request.expected_task_snapshot || !taskSnapshotsEqual(task.snapshot, request.expected_task_snapshot)) {
      await finishConnectorWrite(entry, true, assert);
      throw new Error('agenda_changed');
    }
  }
  const receiptRequest = { action: 'receipt' as const, operation_id: String(entry.body.operation_id),
    ...(request.mirror_context ? { mirror_context: request.mirror_context } : {}) };
  let result: AgendaResponse;
  try {
    result = await invokeAgendaAction(claim.created ? request : receiptRequest, options.ownerId, assert);
  } catch (cause) {
    if (cause instanceof AgendaExecutorError && cause.safe_retry && (claim.created || cause.confirmed_failed)) {
      await finishConnectorWrite(entry, true, assert); assert();
    }
    throw cause;
  }
  assert();
  if (!claim.created && isConfirmedAgendaReceiptFailure(entry.body, result)) {
    await finishConnectorWrite(entry, true, assert); assert(); throw new Error('agenda_write_failed');
  }
  const proven = claim.created ? hasConfirmedConnectorResult(entry.body, result, options.ownerId)
    : hasConfirmedAgendaReceiptResult(entry.body, result, options.ownerId);
  if (!proven) throw new Error('agenda_result_unconfirmed');
  await agendaOwnerSession(options.ownerId, assert);
  await finishConnectorWrite(entry, false, assert); assert();
  const lookup = claim.created ? await invokeAgendaAction(receiptRequest, options.ownerId, assert) : result;
  if (!hasConfirmedAgendaReceiptResult(entry.body, lookup, options.ownerId)) throw new Error('agenda_result_unconfirmed');
  assert(); await agendaOwnerSession(options.ownerId, assert);
  if (request.mirror_context) {
    const receipt = mirrorAgendaReceiptReference({ ...entry, status: 'succeeded' }, {
      userId: options.ownerId, sessionId: request.mirror_context.session_id, actionId: request.mirror_context.action_id,
    });
    if (!receipt || !options.onReceipt || await options.onReceipt(receipt) !== true) throw new Error('agenda_ack_pending');
    assert(); await agendaOwnerSession(options.ownerId, assert);
  }
  await finishConnectorWrite(entry, true, assert); assert();
  publishAgendaRefresh(options.ownerId);
  return { ...lookup, action: request.action, replayed: !claim.created || result.replayed === true };
}
